import type { PriorityType } from "@productlane/ui"

// Same order and digits as Linear's priority picker: "none" is 0.
export const IMPORTANCE_OPTIONS: ReadonlyArray<PriorityType> = [
	"UNKNOWN",
	"HIGH",
	"MEDIUM",
	"LOW",
]

export const MAX_OPTION_DIGIT = 9

export function optionDigit(index: number): string | undefined {
	return index <= MAX_OPTION_DIGIT ? String(index) : undefined
}

export function optionForDigit<T>(
	options: ReadonlyArray<T>,
	key: string,
): T | undefined {
	if (!/^[0-9]$/.test(key)) return undefined
	return options[Number(key)]
}

interface AssignableMember {
	userId: string
	user?: { name?: string | null } | null
}

export interface AssigneeOption<T> {
	userId: string | null
	member: T | null
}

/** "No assignee" first, then you, then everyone else by name, so the digits stay put. */
export function assigneeOptions<T extends AssignableMember>(
	teamMembers: ReadonlyArray<T>,
	sessionUserId: string | undefined,
): Array<AssigneeOption<T>> {
	const me = teamMembers.find((m) => m.userId === sessionUserId)
	const others = teamMembers
		.filter((m) => m.userId !== sessionUserId)
		.sort((a, b) =>
			(a.user?.name ?? "").localeCompare(b.user?.name ?? "", undefined, {
				sensitivity: "base",
			}),
		)
	return [
		{ userId: null, member: null },
		...(me ? [{ userId: me.userId, member: me }] : []),
		...others.map((m) => ({ userId: m.userId, member: m })),
	]
}

/** The digit keys pick an option only until the user starts typing a search. */
export function shouldPickByDigit(
	e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey">,
	commandText: string,
): boolean {
	if (commandText || e.metaKey || e.ctrlKey || e.altKey) return false
	return /^[0-9]$/.test(e.key)
}

export type AskAssistantTarget =
	| { kind: "thread"; threadId: string }
	| { kind: "workspace"; navigateTo: string | null }

/** A thread asks its own copilot; anywhere else asks the inbox one, which lives on /threads. */
export function askAssistantTarget({
	threadId,
	pathname,
}: {
	threadId: string | null
	pathname: string
}): AskAssistantTarget {
	if (threadId) return { kind: "thread", threadId }
	const onInbox = pathname === "/threads" || pathname.startsWith("/threads/")
	return { kind: "workspace", navigateTo: onInbox ? null : "/threads" }
}

/** Tab hands the palette to the assistant only from the root list, never mid sub-page. */
export function shouldAskAssistant(
	e: Pick<KeyboardEvent, "key" | "shiftKey" | "metaKey" | "ctrlKey" | "altKey">,
	{ page, canAsk }: { page: string | undefined; canAsk: boolean },
): boolean {
	if (e.key !== "Tab" || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) {
		return false
	}
	return canAsk && !page
}
